import React from 'react';
import { getIn } from 'timm';
import moment from 'moment-timezone';
import {
  vendorInvoiceStatusTypes,
  createVendorPayoutFields,
  findProcessedPayouts,
  getPayouts,
} from 'x/VendorPayouts';
import { payoutStatusTypes, globals, track } from 'x/modules';
import { getLastElementOf, getFormattedAmount, isEmpty } from 'x/modules/util';
import { Dot } from 'x/ui';
import {
  vendorPortalInvoiceStatusTypes,
  vendorPortalMerchantIdsMap,
  notesToMerchantFieldName,
  vendorPreferencesApiMapping,
} from './constants';

export const isInvoicePartiallyPaid = (invoice) =>
  invoice.status === vendorPortalInvoiceStatusTypes.partiallyPaid;

export const isInvoicePaid = (invoice) => invoice.status === vendorPortalInvoiceStatusTypes.paid;

export const getUTRNumbers = (vendorInvoice) => {
  const payouts = getPayouts(vendorInvoice);
  if (payouts?.length) {
    return payouts.reduce((utrArray, { utr }) => {
      return utr ? utrArray.concat(utr) : utrArray;
    }, []);
  }
  return [];
};

export function isInvoiceMarkedAsPaid(invoice) {
  return invoice.status === vendorPortalInvoiceStatusTypes.paid && !!invoice.marked_as_paid;
}

export function isInvoiceManuallyPaid(invoice) {
  return !!invoice?.marked_as_paid;
}

export const convertInvoiceToTimelineEvents = ({ vendorInvoice: invoice, merchantName }) => {
  const items = [];
  const isPaid = isInvoicePaid(invoice);
  const isMarkedAsPaid = isInvoiceMarkedAsPaid(invoice);

  if (invoice.invoice_date) {
    items.push({
      status: vendorInvoiceStatusTypes.unpaid,
      timestamp: invoice.invoice_date,
      text: 'Issued on',
    });
  }

  if (invoice.draft_created_at) {
    items.push({
      status: vendorInvoiceStatusTypes.draft,
      timestamp: invoice.draft_created_at,
      text: `Sent to ${merchantName}`,
    });
  } else if (invoice.created_at) {
    items.push({
      status: vendorInvoiceStatusTypes.draft,
      timestamp: invoice.created_at,
      text: `Sent to ${merchantName}`,
    });
  }

  if (invoice.unpaid_at) {
    items.push({
      status: vendorInvoiceStatusTypes.unpaid,
      timestamp: invoice.unpaid_at,
      text: `Accepted by ${merchantName}`,
    });
  }

  const payouts = getPayouts(invoice);
  const processedPayouts = findProcessedPayouts(invoice);
  const lastProcessedPayout = getLastElementOf(processedPayouts);
  const isInvoiceFullPaid = !isEmpty(lastProcessedPayout) && isPaid;

  for (let i = 0; i <= payouts.length - 1; i++) {
    const payout = payouts[i];

    if (payout) {
      if (payout.status === payoutStatusTypes.scheduled || payout.scheduled_at) {
        const scheduledTime = moment.unix(payout.scheduled_at).format('DD MMM, ‘YY');

        items.push({
          status: vendorInvoiceStatusTypes.scheduled,
          timestamp: payout.created_at,
          text: `Scheduled for ${scheduledTime}`,
          subText: (
            <span className="text-white-o-60 break-all">
              <span className="text-white-o-40">{getFormattedAmount(payout.amount, true)}</span>
            </span>
          ),
        });
      }

      if (
        payout.status === payoutStatusTypes.processed &&
        !(isInvoiceFullPaid && lastProcessedPayout.id === payout.id)
      ) {
        items.push({
          status: vendorInvoiceStatusTypes.partiallyPaid,
          timestamp: payout.processed_at,
          text: 'Partially paid',
          subText: (
            <span className="text-white-o-60 break-all">
              <span className="text-white-o-40 flex">
                {getFormattedAmount(payout.amount, true)}
                {payout?.utr && (
                  <>
                    <Dot radius="5" className="mt-2 ml-2 mr-2 bg-white-o-20" />
                    UTR: {payout.utr}
                  </>
                )}
              </span>
            </span>
          ),
        });
      }
    }
  }

  if (invoice.status === vendorPortalInvoiceStatusTypes.rejected) {
    const comment = getIn(invoice, ['cancellation_reason']);

    items.push({
      status: vendorInvoiceStatusTypes.cancelled,
      timestamp: invoice.cancelled_at,
      text: `Rejected by ${merchantName}`,
      subText: (
        <span className="leading-tight">
          Reason: {comment && <span className="font-bold">&quot;{comment}&quot;</span>}
        </span>
      ),
    });
  }

  if (isPaid) {
    if (isMarkedAsPaid) {
      const utr = getIn(invoice, ['manually_paid_metadata', 'utr']);
      const amount = invoice?.amount_paid;
      items.push({
        status: vendorInvoiceStatusTypes.paid,
        timestamp: invoice.paid_at,
        text: 'Paid',
        subText: (
          <span className="text-white-o-60 break-all">
            <span className="text-white-o-40 flex">
              {getFormattedAmount(amount, true)}
              {utr && (
                <>
                  <Dot radius="5" className="mt-2 ml-2 mr-2 bg-white-o-20" />
                  UTR: {utr}
                </>
              )}
            </span>
          </span>
        ),
      });
    } else {
      items.push({
        status: vendorInvoiceStatusTypes.paid,
        timestamp: invoice.paid_at,
        text: 'Paid',
        subText: (
          <span className="text-white-o-60 break-all">
            <span className="text-white-o-40 flex">
              {getFormattedAmount(lastProcessedPayout?.amount, true)}
              {lastProcessedPayout?.utr && (
                <>
                  <Dot radius="5" className="mt-2 ml-2 mr-2 bg-white-o-20" />
                  UTR: {lastProcessedPayout.utr}
                </>
              )}
            </span>
          </span>
        ),
      });
    }
  }

  return items;
};

export const getVendorPortalMerchantId = () => {
  let vendorPortalMerchantId = null;

  const env = globals.get('env');
  if (env === 'dev') {
    const host = globals.get('host');

    if (host.includes('x-func.np.razorpay.in')) {
      vendorPortalMerchantId = vendorPortalMerchantIdsMap.func;
    }

    if (host.includes('x.np.razorpay.in')) {
      vendorPortalMerchantId = vendorPortalMerchantIdsMap.beta;
    }

    if (host.includes('x.razorpay.com')) {
      vendorPortalMerchantId = vendorPortalMerchantIdsMap.prod;
    }

    if (host.includes('x.dev.razorpay.in')) {
      vendorPortalMerchantId = vendorPortalMerchantIdsMap.devstack;
    }
  } else {
    vendorPortalMerchantId = vendorPortalMerchantIdsMap?.[env];
  }

  return vendorPortalMerchantId ? vendorPortalMerchantId : '';
};

export const isVendorPortalEnabled = (activeExperiments) => {
  const { rxVendorPortalRollout } = activeExperiments;
  return rxVendorPortalRollout === true;
};

export const trackFieldChange = (labelName) => {
  let ljEventName = '';

  switch (labelName) {
    case createVendorPayoutFields.INVOICE_ISSUE_DATE:
      ljEventName = 'vp.vendor_portal_create_payment_request.edit_invoice_date';
      break;
    case createVendorPayoutFields.INVOICE_NUMBER:
      ljEventName = 'vp.vendor_portal_create_payment_request.add_invoice_no.';
      break;
    case createVendorPayoutFields.DUE_DATE:
      ljEventName = 'vp.vendor_portal_create_payment_request.edit_due_date';
      break;
    case createVendorPayoutFields.TOTAL_AMOUNT:
      ljEventName = 'vp.vendor_portal_create_payment_request.add_total_amount';
      break;
    case createVendorPayoutFields.SUB_TOTAL:
      ljEventName = 'vp.vendor_portal_create_payment_request.edit_subtotal';
      break;
    case createVendorPayoutFields.TDS_CATEGORY:
      ljEventName = 'vp.vendor_portal_create_payment_request.edit_TDS_category';
      break;
    case createVendorPayoutFields.TDS_AMOUNT:
      ljEventName = 'vp.vendor_portal_create_payment_request.edit_TDS_amount';
      break;
    case createVendorPayoutFields.GST_AMOUNT:
      ljEventName = 'vp.vendor_portal_create_payment_request.edit_GST';
      break;
    case createVendorPayoutFields.DESCRIPTION:
      ljEventName = 'vp.vendor_portal_create_payment_request.add_description';
      break;
    case notesToMerchantFieldName:
      ljEventName = 'vp.vendor_portal_create_payment_request.add_note_to_merchant';
      break;
    case vendorPreferencesApiMapping.contact:
      ljEventName = 'vp.vendor_portal.onboarding_contactno';
      break;
    case vendorPreferencesApiMapping.gstin:
      ljEventName = 'vp.vendor_portal.onboarding_GSTIN';
      break;
    case vendorPreferencesApiMapping.pan:
      ljEventName = 'vp.vendor_portal.onboarding_edit_PAN';
      break;
    default:
      break;
  }

  track({
    ljEventName,
    ljEventType: 'initiated',
  });
};

export function formatTDSValue(tds) {
  return `${tds.code} ${tds.name}`;
}

export const vendorPortalCreateInvoiceTourSteps = [
  {
    selector: '.empty-view-create-payment-request',
    content: <span className="block mb-3 mt-3">Create a payment request over here</span>,
    toolTipPlacement: 'right',
    customStyle: {
      top: -38,
      left: 1,
    },
  },
];

export const vendorPortalExistingInvoiceTourSteps = [
  {
    selector: '.select-actions .heading',
    content: (
      <div className="pr-2">
        <span className="block mb-3 mt-3 font-bold">
          <span role="img" aria-label="">
            ⚡
          </span>{' '}
          All your invoices at a single place
        </span>
        <span className="block mb-3 mt-3">We found some past invoices from you</span>
      </div>
    ),
    toolTipPlacement: 'bottom',
    customStyle: {
      top: 30,
      left: 14,
    },
  },
  {
    selector: '.create-payment-request-button',
    content: (
      <span className="block mb-3 mt-3">
        Start by adding a new payment request by clicking on this button{' '}
      </span>
    ),
    toolTipPlacement: 'left',
    customStyle: {
      top: -50,
      left: -10,
    },
  },
];
